// AF-3.16 — Brand Growth OS frontend API client.
//
// Thin, additive wrapper over the real AF-2.1-AF-3.15 backend routes
// already implemented in server.py. This module introduces NO new
// backend behavior -- every function here maps 1:1 to an existing,
// already-tested route. Response shapes are not reinterpreted: each
// function returns the raw axios promise, and callers read the exact
// backend field names (objective, guardrail_metrics, control_status,
// gap_status, etc.) verbatim -- never renamed, never recomputed,
// never defaulted to a fabricated value.
//
// workspace_id and client_id are the same identifier throughout this
// system (every backend service's own _public() sets
// out["workspace_id"] = out.get("client_id")) -- this client always
// calls it workspaceId for consistency with the URL path segment name.

import api from './api';

const base = (workspaceId) => `/intelligence/${workspaceId}`;

// ─── AF-2.1: Entities (graph entity CRUD lives under /v3/graph, the
// only place a real "list entities" route exists) ───
export const entitiesAPI = {
  list: (workspaceId, { entityType, includeArchived = false, q } = {}) =>
    api.get(`/v3/graph/entities/${workspaceId}`, { params: { entity_type: entityType, include_archived: includeArchived, q } }),
  get: (workspaceId, entityId) => api.get(`/v3/graph/entities/${workspaceId}/${entityId}`),
  create: (workspaceId, data) => api.post(`/v3/graph/entities/${workspaceId}`, data),
};

// ─── AF-2.1: Entity context (frozen, exact-key-set contract) ───
export const entityContextAPI = {
  get: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/entity/${entityType}/${entityId}/context`),
};

// ─── AF-2.2-2.6: Signals / Diagnoses / Opportunities / Recommendations / Action Plans ───
export const signalsAPI = {
  listForEntity: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/entity/${entityType}/${entityId}/signals`),
  // Brand Console (Signals page) -- same reasoning as
  // recommendationsAPI.listForWorkspace below: GET
  // /intelligence/{workspace_id}/signals (server.py's list_signals_route)
  // already accepts entity_type/entity_id/signal_type/status/severity as
  // fully OPTIONAL query params, so calling it with none returns every
  // real signal scoped to (tenant_id, client_id) -- the same "viewer"
  // RBAC and get_verified_client tenant/client isolation as every other
  // call in this file. No new backend behavior.
  listForWorkspace: (workspaceId, params = {}) => api.get(`${base(workspaceId)}/signals`, { params }),
};

export const diagnosesAPI = {
  listForEntity: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/diagnoses`, { params: { entity_type: entityType, entity_id: entityId } }),
  get: (workspaceId, diagnosisId) => api.get(`${base(workspaceId)}/diagnoses/${diagnosisId}`),
  // Brand Console (DiagnosesPage.js) -- GET /intelligence/{workspace_id}/diagnoses
  // (server.py's list_diagnoses_route / DiagnosisService.query_diagnoses)
  // already accepts signal_id/entity_type/entity_id/classification/status
  // as fully optional query params, so calling it with none returns every
  // real, deterministic causal hypothesis Sahib has generated for the
  // workspace -- the same "viewer" RBAC and get_verified_client
  // tenant/client isolation as every other call in this file. No new
  // backend behavior; zero frontend consumer until now.
  listForWorkspace: (workspaceId, params = {}) => api.get(`${base(workspaceId)}/diagnoses`, { params }),
  // P2.5 -- the founder-facing trigger for AF-2.3. server.py's
  // diagnose_signal_route (POST /intelligence/{workspace_id}/diagnoses/diagnose,
  // require_role("editor") -- a fresh founder is "owner", above "editor")
  // has existed and been tested since AF-2.3 but had no frontend caller,
  // so a real founder's Signal never reached a Diagnosis. Same {signal_id}
  // body the route already validates; DiagnosisService.diagnose_signal is
  // idempotent (re-running soft-DISMISSes the prior ACTIVE set and writes
  // a fresh one). No new backend behavior.
  diagnose: (workspaceId, signalId) => api.post(`${base(workspaceId)}/diagnoses/diagnose`, { signal_id: signalId }),
};

export const opportunitiesAPI = {
  listForEntity: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/entity/${entityType}/${entityId}/opportunities`),
  get: (workspaceId, opportunityId) => api.get(`${base(workspaceId)}/opportunities/${opportunityId}`),
  // Brand Console (Recommendations page) -- same reasoning as
  // recommendationsAPI.listForWorkspace: GET
  // /intelligence/{workspace_id}/opportunities already accepts every
  // filter as optional, so this returns every real opportunity for the
  // workspace, used only to resolve a recommendation's opportunity_id
  // into its human-readable opportunity_type/title. No new backend behavior.
  listForWorkspace: (workspaceId) => api.get(`${base(workspaceId)}/opportunities`),
  // P2.5 -- the founder-facing trigger for AF-2.4. server.py's
  // detect_opportunities_route (POST /intelligence/{workspace_id}/opportunities/detect,
  // require_role("editor")) has existed and been tested since AF-2.4 but
  // had no frontend caller. It is entity-scoped (it surveys all of one
  // entity's open signals + ACTIVE diagnoses), so the Signals page passes
  // the signal's own entity_type/entity_id. OpportunityService.detect_opportunities
  // is idempotent (_find_open refreshes an existing open opportunity in
  // place; a RESOLVED/DISMISSED one that recurs gets a new id). No new
  // backend behavior.
  detect: (workspaceId, entityType, entityId) =>
    api.post(`${base(workspaceId)}/opportunities/detect`, { entity_type: entityType, entity_id: entityId }),
};

export const recommendationsAPI = {
  listForEntity: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/entity/${entityType}/${entityId}/recommendations`),
  get: (workspaceId, recommendationId) => api.get(`${base(workspaceId)}/recommendations/${recommendationId}`),
  // RecommendationsAwaitingCard remediation -- the plain GET
  // /intelligence/{workspace_id}/recommendations route (server.py's
  // list_recommendations_route) already accepts entity_type/entity_id/
  // opportunity_id/recommendation_type/status as fully OPTIONAL query
  // params (confirmed by direct read) and, with none supplied, returns
  // every real recommendation scoped to (tenant_id, client_id) only --
  // the exact same RBAC ("viewer") and tenant/client verification
  // (get_verified_client) as every other call in this file, no new
  // backend behavior. listForEntity() requires a pre-resolved entity,
  // which most real clients do not yet have (confirmed against the live
  // database: of 38 real clients, no entity_type has coverage above 7 --
  // there is no reliable default entity to fall back to), so a
  // workspace-wide read is the only accurate way to answer "does this
  // workspace have anything awaiting a decision" without inventing or
  // guessing an entity.
  listForWorkspace: (workspaceId) => api.get(`${base(workspaceId)}/recommendations`),
  // Sahib Bridge -- the Intelligence -> Action write surface. Every one of
  // these already existed as a real, RBAC-gated, tenant-scoped server.py
  // route (RecommendationService.detect_recommendations/
  // acknowledge_recommendation/approve_recommendation/reject_recommendation/
  // dismiss_recommendation/update_recommendation) with zero frontend
  // wrapper until now -- no new backend behavior is introduced here.
  forOpportunity: (workspaceId, opportunityId) => api.get(`${base(workspaceId)}/opportunities/${opportunityId}/recommendations`),
  detect: (workspaceId, opportunityId) => api.post(`${base(workspaceId)}/recommendations/detect`, { opportunity_id: opportunityId }),
  update: (workspaceId, recommendationId, data) => api.patch(`${base(workspaceId)}/recommendations/${recommendationId}`, data),
  acknowledge: (workspaceId, recommendationId) => api.post(`${base(workspaceId)}/recommendations/${recommendationId}/acknowledge`),
  approve: (workspaceId, recommendationId) => api.post(`${base(workspaceId)}/recommendations/${recommendationId}/approve`),
  reject: (workspaceId, recommendationId) => api.post(`${base(workspaceId)}/recommendations/${recommendationId}/reject`),
  dismiss: (workspaceId, recommendationId) => api.post(`${base(workspaceId)}/recommendations/${recommendationId}/dismiss`),
};

export const actionPlansAPI = {
  listForEntity: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/entity/${entityType}/${entityId}/action-plans`),
  get: (workspaceId, actionPlanId) => api.get(`${base(workspaceId)}/action-plans/${actionPlanId}`),
  // Sahib Bridge -- same discipline as recommendationsAPI above: every
  // method wraps an existing, already-tested, already-concurrency-audited
  // ActionPlanService route (detect_action_plans/update_action_plan/
  // approve_action_plan/cancel_action_plan). No new backend behavior.
  byRecommendation: (workspaceId, recommendationId) => api.get(`${base(workspaceId)}/recommendations/${recommendationId}/action-plans`),
  detect: (workspaceId, recommendationId) => api.post(`${base(workspaceId)}/action-plans/detect`, { recommendation_id: recommendationId }),
  update: (workspaceId, actionPlanId, data) => api.patch(`${base(workspaceId)}/action-plans/${actionPlanId}`, data),
  approve: (workspaceId, actionPlanId) => api.post(`${base(workspaceId)}/action-plans/${actionPlanId}/approve`),
  cancel: (workspaceId, actionPlanId) => api.post(`${base(workspaceId)}/action-plans/${actionPlanId}/cancel`),
  // Brand Console (OutcomesPage.js name-resolution, ActionPlansPage.js's
  // own viewer) -- same reasoning as recommendationsAPI/
  // opportunitiesAPI.listForWorkspace above: GET
  // /intelligence/{workspace_id}/action-plans (server.py's
  // list_action_plans_route) already accepts entity_type/entity_id/
  // recommendation_id/action_plan_type/status/readiness as fully OPTIONAL
  // query params, so this returns every real action plan for the
  // workspace when called with none. No new backend behavior.
  listForWorkspace: (workspaceId, params = {}) => api.get(`${base(workspaceId)}/action-plans`, { params }),
};

// Sahib Bridge -- the compact, read-only data bridge between
// SahibBriefingPage (executive/decision-facing) and the real Growth OS
// Opportunity/Recommendation/Action-Plan state (operational). A thin
// wrapper over GET /intelligence/{workspace_id}/bridge-summary
// (server.py's get_bridge_summary_route), itself a thin wrapper over
// sahib_intelligence_bridge.build_bridge_summary() -- no new backend
// business logic, no second source of truth, no new status value.
export const bridgeSummaryAPI = {
  get: (workspaceId) => api.get(`${base(workspaceId)}/bridge-summary`),
};

// ─── AF-2.8: Measurement / Outcome / Learning ───
export const measurementsAPI = {
  listForEntity: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/entity/${entityType}/${entityId}/measurements`),
  // Brand Console DATA pages -- GET /intelligence/{workspace_id}/measurements
  // (server.py's list_measurements_route / MeasurementService.query_measurements)
  // already accepts entity_type/entity_id/metric_key/source_type/status
  // as fully optional query params. Passing source_type: "CONNECTOR"
  // returns exactly the real, connector-synced records the intelligence
  // bridge writes -- which provider each record came from is read from
  // its own source_reference field by the calling page, never decided or
  // filtered here; this client stays provider-agnostic, same as every
  // other function in this file. No new backend behavior; same "viewer"
  // RBAC and tenant/client isolation as every other call in this file.
  listForWorkspace: (workspaceId, params = {}) => api.get(`${base(workspaceId)}/measurements`, { params }),
};
export const outcomesAPI = {
  listForEntity: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/entity/${entityType}/${entityId}/outcomes`),
  get: (workspaceId, outcomeId) => api.get(`${base(workspaceId)}/outcomes/${outcomeId}`),
  // Brand Console (OutcomesPage.js) -- GET /intelligence/{workspace_id}/outcomes
  // (server.py's list_outcomes_route / OutcomeService.query_outcomes)
  // already accepts entity_type/entity_id/action_plan_id/recommendation_id/
  // result/status as fully optional query params, so calling it with none
  // returns every real, deterministically-evaluated outcome scoped to
  // (tenant_id, client_id) -- the same "viewer" RBAC and
  // get_verified_client tenant/client isolation as every other call in
  // this file. No new backend behavior; zero frontend consumer until now.
  listForWorkspace: (workspaceId, params = {}) => api.get(`${base(workspaceId)}/outcomes`, { params }),
};
export const learningsAPI = {
  listForEntity: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/entity/${entityType}/${entityId}/learnings`),
  get: (workspaceId, learningId) => api.get(`${base(workspaceId)}/learnings/${learningId}`),
};

// ─── AF-2.9: Environment / Governance ───
// AF-3.18 — read-only connector foundation. Note the path prefix is
// `/connectors/{workspace_id}`, not `/intelligence/{workspace_id}` --
// connectors are a separate, isolated module from the AF-2.x/AF-3.x
// intelligence routes.
export const connectorsAPI = {
  list: (workspaceId) => api.get(`/connectors/${workspaceId}`),
  get: (workspaceId, connectorId) => api.get(`/connectors/${workspaceId}/${connectorId}`),
  create: (workspaceId, data) => api.post(`/connectors/${workspaceId}`, data),
  healthCheck: (workspaceId, connectorId) => api.post(`/connectors/${workspaceId}/${connectorId}/health-check`),
  sync: (workspaceId, connectorId, data = {}) => api.post(`/connectors/${workspaceId}/${connectorId}/sync`, data),
  disconnect: (workspaceId, connectorId) => api.post(`/connectors/${workspaceId}/${connectorId}/disconnect`),
};

// Milestone 5 (Social Organic Agent) -- real, always-visible facts
// (social_intelligence.py's SocialFindingService.get_facts_for_client),
// separate from /prepared-work: facts are shown on the AI CGO Home
// regardless of whether a prepared-work finding currently exists.
export const socialFactsAPI = {
  get: (workspaceId) => api.get(`/social/facts/${workspaceId}`),
};

// AF-3.17.1 credential references, needed by the AF-3.18 connect flow
// (a connector must reference an existing CredentialReference; this
// never sends a raw secret -- see AF-3.17.1's own rejection of any
// secret-shaped field in this payload).
export const credentialReferencesAPI = {
  list: (workspaceId) => api.get(`/security/${workspaceId}/credential-references`),
  create: (workspaceId, data) => api.post(`/security/${workspaceId}/credential-references`, data),
};

export const environmentAPI = {
  list: (workspaceId, params = {}) => api.get(`${base(workspaceId)}/environment`, { params }),
  get: (workspaceId, recordId) => api.get(`${base(workspaceId)}/environment/${recordId}`),
  history: (workspaceId, recordId) => api.get(`${base(workspaceId)}/environment/${recordId}/history`),
  forEntity: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/entities/${entityType}/${entityId}/environment`),
  impacts: (workspaceId, params = {}) => api.get(`${base(workspaceId)}/environment/impacts`, { params }),
  evaluate: (workspaceId, data) => api.post(`${base(workspaceId)}/environment/evaluate`, data),
};

// ─── AF-3.10: Creator Command Center (aggregates AF-3.1-3.9) ───
export const creatorCommandAPI = {
  preview: (workspaceId, payload) => api.post(`${base(workspaceId)}/creator/commands/preview`, payload),
  create: (workspaceId, payload) => api.post(`${base(workspaceId)}/creator/commands/create`, payload),
  list: (workspaceId, params = {}) => api.get(`${base(workspaceId)}/creator/commands`, { params }),
  get: (workspaceId, commandId) => api.get(`${base(workspaceId)}/creator/commands/${commandId}`),
  production: (workspaceId, commandId) => api.get(`${base(workspaceId)}/creator/commands/${commandId}/production`),
  distribution: (workspaceId, commandId) => api.get(`${base(workspaceId)}/creator/commands/${commandId}/distribution`),
  measurement: (workspaceId, commandId) => api.get(`${base(workspaceId)}/creator/commands/${commandId}/measurement`),
  learning: (workspaceId, commandId) => api.get(`${base(workspaceId)}/creator/commands/${commandId}/learning`),
  productionQueue: (workspaceId) => api.get(`${base(workspaceId)}/creator/commands/production-queue`),
  approvalQueue: (workspaceId) => api.get(`${base(workspaceId)}/creator/commands/approval-queue`),
  distributionQueue: (workspaceId) => api.get(`${base(workspaceId)}/creator/commands/distribution-queue`),
  measurementQueue: (workspaceId) => api.get(`${base(workspaceId)}/creator/commands/measurement-queue`),
  learningQueue: (workspaceId) => api.get(`${base(workspaceId)}/creator/commands/learning-queue`),
};

// ─── AF-3.11: Brand Growth Intelligence ───
// No dedicated preview route exists for AF-3.11 -- create_snapshot()
// itself dedupes on unchanged state (returns the same document rather
// than writing a duplicate), so calling `create` directly is the correct
// and only way to read a snapshot; never fabricate a preview endpoint
// that doesn't exist in server.py.
export const brandGrowthAPI = {
  create: (workspaceId, payload) => api.post(`${base(workspaceId)}/growth/snapshot`, payload),
  refresh: (workspaceId, payload) => api.post(`${base(workspaceId)}/growth/refresh`, payload),
  current: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/growth/current`, { params: { entity_type: entityType, entity_id: entityId } }),
  opportunities: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/growth/opportunities`, { params: { entity_type: entityType, entity_id: entityId } }),
  experiments: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/growth/experiments`, { params: { entity_type: entityType, entity_id: entityId } }),
  learnings: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/growth/learnings`, { params: { entity_type: entityType, entity_id: entityId } }),
  environmentImpact: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/growth/environment-impact`, { params: { entity_type: entityType, entity_id: entityId } }),
  nextAction: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/growth/next-action`, { params: { entity_type: entityType, entity_id: entityId } }),
  state: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/growth/state`, { params: { entity_type: entityType, entity_id: entityId } }),
};

// ─── AF-3.12: Brand Outcome Control ───
export const outcomeControlAPI = {
  create: (workspaceId, payload) => api.post(`${base(workspaceId)}/outcome-control/snapshot`, payload),
  current: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/outcome-control/current`, { params: { entity_type: entityType, entity_id: entityId } }),
  objective: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/outcome-control/objective`, { params: { entity_type: entityType, entity_id: entityId } }),
  performance: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/outcome-control/performance`, { params: { entity_type: entityType, entity_id: entityId } }),
  gaps: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/outcome-control/gaps`, { params: { entity_type: entityType, entity_id: entityId } }),
  interventions: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/outcome-control/interventions`, { params: { entity_type: entityType, entity_id: entityId } }),
  experiments: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/outcome-control/experiments`, { params: { entity_type: entityType, entity_id: entityId } }),
  learnings: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/outcome-control/learnings`, { params: { entity_type: entityType, entity_id: entityId } }),
  environmentRisk: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/outcome-control/environment-risk`, { params: { entity_type: entityType, entity_id: entityId } }),
  decision: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/outcome-control/decision`, { params: { entity_type: entityType, entity_id: entityId } }),
};

// ─── AF-3.13: Brand Growth Planning ───
export const growthPlanAPI = {
  create: (workspaceId, payload) => api.post(`${base(workspaceId)}/growth/plans`, payload),
  preview: (workspaceId, payload) => api.post(`${base(workspaceId)}/growth/plans/preview`, payload),
  list: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/growth/plans`, { params: { entity_type: entityType, entity_id: entityId } }),
  currentFor: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/growth/${entityType}/${entityId}/plan`),
  priorities: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/growth/${entityType}/${entityId}/priorities`),
  workstreams: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/growth/${entityType}/${entityId}/workstreams`),
  activate: (workspaceId, planId) => api.post(`${base(workspaceId)}/growth/plans/${planId}/activate`),
  hold: (workspaceId, planId) => api.post(`${base(workspaceId)}/growth/plans/${planId}/hold`),
};

// ─── AF-3.19: Creator chain (Strategy/Idea/Review/Decision/Distribution) ───
// Additive only -- every path below already exists in server.py (AF-3.3-
// AF-3.7's own route wiring). This closes the frontend-orphan gap the
// AF-3.19 audit identified: these routes previously had zero JS client
// anywhere in the codebase. No new backend route, no new computation --
// callers read the exact backend field names (strategy_status,
// governance_status, approval_status, etc.) verbatim, same convention as
// the rest of this file.
export const creativeStrategyAPI = {
  list: (workspaceId, params = {}) => api.get(`${base(workspaceId)}/creator/strategies`, { params }),
  get: (workspaceId, strategyId) => api.get(`${base(workspaceId)}/creator/strategies/${strategyId}`),
  context: (workspaceId, strategyId) => api.get(`${base(workspaceId)}/creator/strategies/${strategyId}/context`),
  forOpportunity: (workspaceId, opportunityId) => api.get(`${base(workspaceId)}/creator/opportunities/${opportunityId}/strategies`),
};

export const creativeIdeaAPI = {
  list: (workspaceId, params = {}) => api.get(`${base(workspaceId)}/creator/ideas`, { params }),
  get: (workspaceId, ideaId) => api.get(`${base(workspaceId)}/creator/ideas/${ideaId}`),
  context: (workspaceId, ideaId) => api.get(`${base(workspaceId)}/creator/ideas/${ideaId}/context`),
  forStrategy: (workspaceId, strategyId) => api.get(`${base(workspaceId)}/creator/strategies/${strategyId}/ideas`),
};

export const creativeReviewAPI = {
  list: (workspaceId, params = {}) => api.get(`${base(workspaceId)}/creator/reviews`, { params }),
  get: (workspaceId, reviewId) => api.get(`${base(workspaceId)}/creator/reviews/${reviewId}`),
  forStrategy: (workspaceId, strategyId) => api.get(`${base(workspaceId)}/creator/reviews/strategy/${strategyId}`),
  forIdea: (workspaceId, ideaId) => api.get(`${base(workspaceId)}/creator/reviews/idea/${ideaId}`),
};

export const creativeDecisionAPI = {
  list: (workspaceId, params = {}) => api.get(`${base(workspaceId)}/creator/decisions`, { params }),
  get: (workspaceId, decisionId) => api.get(`${base(workspaceId)}/creator/decisions/${decisionId}`),
  forStrategy: (workspaceId, strategyId) => api.get(`${base(workspaceId)}/creator/strategies/${strategyId}/decision`),
  productionPackage: (workspaceId, decisionId) => api.get(`${base(workspaceId)}/creator/decisions/${decisionId}/production-package`),
};

export const creatorDistributionAPI = {
  list: (workspaceId, params = {}) => api.get(`${base(workspaceId)}/creator/distribution`, { params }),
  get: (workspaceId, targetId) => api.get(`${base(workspaceId)}/creator/distribution/${targetId}`),
  forDecision: (workspaceId, decisionId) => api.get(`${base(workspaceId)}/creator/decisions/${decisionId}/distribution`),
  publishingPackage: (workspaceId, targetId) => api.get(`${base(workspaceId)}/creator/distribution/${targetId}/publishing-package`),
};

// ─── AF-3.14: Brand Outcome Control Plane (the primary Command Center source) ───
export const controlPlaneAPI = {
  create: (workspaceId, payload) => api.post(`${base(workspaceId)}/outcome-control-plane/state`, payload),
  preview: (workspaceId, payload) => api.post(`${base(workspaceId)}/outcome-control-plane/state/preview`, payload),
  current: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/outcome-control-plane/current`, { params: { entity_type: entityType, entity_id: entityId } }),
  objective: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/outcome-control-plane/objective`, { params: { entity_type: entityType, entity_id: entityId } }),
  performance: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/outcome-control-plane/performance`, { params: { entity_type: entityType, entity_id: entityId } }),
  experiments: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/outcome-control-plane/experiments`, { params: { entity_type: entityType, entity_id: entityId } }),
  learnings: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/outcome-control-plane/learnings`, { params: { entity_type: entityType, entity_id: entityId } }),
  environment: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/outcome-control-plane/environment`, { params: { entity_type: entityType, entity_id: entityId } }),
  control: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/outcome-control-plane/control`, { params: { entity_type: entityType, entity_id: entityId } }),
  evidence: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/outcome-control-plane/evidence`, { params: { entity_type: entityType, entity_id: entityId } }),
  refresh: (workspaceId, payload) => api.post(`${base(workspaceId)}/outcome-control-plane/refresh`, payload),
  history: (workspaceId, entityType, entityId) => api.get(`${base(workspaceId)}/outcome-control-plane/history`, { params: { entity_type: entityType, entity_id: entityId } }),
};
